import React, { useState, useEffect } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Users,
  Search,
  UserMinus,
  UserPlus,
  ShieldCheck,
  Zap,
  ZapOff,
  CalendarDays,
  Activity,
  ChevronRight,
  Info,
} from 'lucide-react';
import { teamsAPI, agentsAPI } from '../services/api';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Card, CardHeader, CardDescription, CardTitle, CardAction, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from '../components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

// ── Interfaces ────────────────────────────────────────────────────────────────

interface Team {
  id: number;
  name: string;
  description: string | null;
  allow_auto_assign: boolean;
  created_at: string;
  updated_at: string;
}

interface TeamMember {
  id: number;
  user_id: number;
  team_id: number;
  is_admin: boolean;
  user: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    avatar_url?: string;
    role: string;
    availability_status: string;
  };
}

interface Agent {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  avatar_url?: string;
  role: string;
  availability_status: string;
}

interface TeamFormData {
  name: string;
  description: string;
  allow_auto_assign: boolean;
}

// ── Config / helpers ──────────────────────────────────────────────────────────

const AVAIL_DOT: Record<string, string> = {
  available: 'bg-emerald-500',
  busy: 'bg-amber-500',
  offline: 'bg-zinc-400',
};

const AVAIL_BADGE: Record<string, string> = {
  available: 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-800/50 dark:text-emerald-400',
  busy: 'bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-950/40 dark:border-amber-800/50 dark:text-amber-400',
  offline: 'bg-zinc-100 border-zinc-200 text-zinc-500 dark:bg-zinc-800/40 dark:border-zinc-700/50 dark:text-zinc-400',
};

const TEAM_COLORS = [
  'bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300',
  'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
  'bg-cyan-100 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300',
  'bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300',
  'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300',
];

const getTeamColor = (name: string) => {
  const hash = name.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return TEAM_COLORS[hash % TEAM_COLORS.length];
};

const getTeamInitials = (name: string) => {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const formatDate = (iso: string) => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
};

const getRelativeTime = (iso: string): string => {
  try {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return '—';
    const diff = Date.now() - t;
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return mins <= 1 ? 'Just now' : `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(months / 12)}y ago`;
  } catch { return '—'; }
};

// ── Member Facepile ───────────────────────────────────────────────────────────

const MemberFacepile: React.FC<{ members: TeamMember[]; max?: number; size?: number }> = ({
  members, max = 4, size = 20,
}) => {
  const visible = members.filter((m) => m.user).slice(0, max);
  const overflow = Math.max(0, members.filter((m) => m.user).length - max);
  if (visible.length === 0) return null;
  return (
    <div className="flex shrink-0 items-center overflow-visible">
      {visible.map((m, i) => (
        <div
          key={m.id}
          className="rounded-full border-2 border-card overflow-hidden bg-muted"
          style={{ width: size, height: size, marginLeft: i > 0 ? -(size * 0.35) : 0, zIndex: visible.length - i }}
        >
          {m.user.avatar_url ? (
            <img src={m.user.avatar_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-primary/10 text-primary font-semibold"
              style={{ fontSize: size * 0.38 }}>
              {m.user.first_name?.[0]}{m.user.last_name?.[0]}
            </div>
          )}
        </div>
      ))}
      {overflow > 0 && (
        <span className="text-[10px] text-muted-foreground ml-1.5">+{overflow}</span>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const TeamsPage: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [teamMembers, setTeamMembers] = useState<{ [teamId: number]: TeamMember[] }>({});
  const [loading, setLoading] = useState(true);

  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const [memberSearch, setMemberSearch] = useState('');

  const [openDialog, setOpenDialog] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [formData, setFormData] = useState<TeamFormData>({ name: '', description: '', allow_auto_assign: true });

  const [openMembersDialog, setOpenMembersDialog] = useState(false);
  const [membersDialogTeam, setMembersDialogTeam] = useState<Team | null>(null);
  const [selectedAgentIds, setSelectedAgentIds] = useState<number[]>([]);
  const [agentSearchTerm, setAgentSearchTerm] = useState('');

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterAutoAssign, setFilterAutoAssign] = useState<string>('all');

  useEffect(() => {
    fetchTeams();
    fetchAgents();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only initial load

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const data = await teamsAPI.getTeams();
      setTeams(data);
      data.forEach((team: Team) => fetchTeamMembers(team.id));
    } catch {
      toast({ title: 'Error', description: 'Failed to fetch teams', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const data = await agentsAPI.getAgents();
      setAgents(data.agents || data || []);
    } catch { /* silently fail */ }
  };

  const fetchTeamMembers = async (teamId: number) => {
    try {
      const data = await teamsAPI.getTeamMembers(teamId);
      setTeamMembers((prev) => ({ ...prev, [teamId]: data }));
    } catch { /* silently fail */ }
  };

  const formatErrorMessage = (error: any): string => {
    if (error.response?.data) {
      const d = error.response.data.detail;
      if (Array.isArray(d)) return d.map((e: any) => e.msg || JSON.stringify(e)).join(', ');
      if (typeof d === 'string') return d;
      if (typeof d === 'object') return d.msg || d.message || JSON.stringify(d);
    }
    return 'An error occurred';
  };

  // ── Team CRUD ───────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    try {
      if (editingTeam) {
        await teamsAPI.updateTeam(editingTeam.id, formData);
        toast({ title: 'Team updated', description: 'Team updated successfully' });
      } else {
        await teamsAPI.createTeam(formData);
        toast({ title: 'Team created', description: 'Team created successfully' });
      }
      fetchTeams();
      handleCloseDialog();
    } catch (error: any) {
      toast({ title: 'Error', description: formatErrorMessage(error) || 'Failed to save team', variant: 'destructive' });
    }
  };

  const handleDelete = async (team: Team) => {
    try {
      await teamsAPI.deleteTeam(team.id);
      toast({ title: 'Team deleted', description: 'Team deleted successfully' });
      if (selectedTeamId === team.id) setSelectedTeamId(null);
      fetchTeams();
    } catch (error: any) {
      toast({ title: 'Error', description: formatErrorMessage(error) || 'Failed to delete team', variant: 'destructive' });
    } finally {
      setDeleteConfirmOpen(false);
      setTeamToDelete(null);
    }
  };

  const handleOpenDialog = (team?: Team) => {
    if (team) {
      setEditingTeam(team);
      setFormData({ name: team.name, description: team.description || '', allow_auto_assign: team.allow_auto_assign });
    } else {
      setEditingTeam(null);
      setFormData({ name: '', description: '', allow_auto_assign: true });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => { setOpenDialog(false); setEditingTeam(null); };

  // ── Member management ───────────────────────────────────────────────────────

  const handleOpenMembersDialog = (team: Team) => {
    setMembersDialogTeam(team);
    const currentMemberIds = (teamMembers[team.id] || []).filter((m) => m.user).map((m) => m.user.id);
    setSelectedAgentIds(currentMemberIds);
    setAgentSearchTerm('');
    setOpenMembersDialog(true);
  };

  const handleAddAgentsToTeam = async () => {
    if (!membersDialogTeam) return;
    try {
      const currentMembers = teamMembers[membersDialogTeam.id] || [];
      const currentMemberIds = currentMembers.filter((m) => m.user).map((m) => m.user.id);
      const toAdd = selectedAgentIds.filter((id) => !currentMemberIds.includes(id));
      const toRemove = currentMemberIds.filter((id) => !selectedAgentIds.includes(id));
      for (const agentId of toAdd) await teamsAPI.addTeamMember(membersDialogTeam.id, agentId, false);
      for (const agentId of toRemove) await teamsAPI.removeTeamMember(membersDialogTeam.id, agentId);
      toast({ title: 'Members updated', description: 'Team members updated successfully' });
      fetchTeamMembers(membersDialogTeam.id);
      setOpenMembersDialog(false);
      setSelectedAgentIds([]);
    } catch (error: any) {
      toast({ title: 'Error', description: formatErrorMessage(error) || 'Failed to update team members', variant: 'destructive' });
    }
  };

  const handleRemoveAgentFromTeam = async (teamId: number, agentId: number) => {
    try {
      await teamsAPI.removeTeamMember(teamId, agentId);
      toast({ title: 'Agent removed', description: 'Agent removed from team' });
      fetchTeamMembers(teamId);
    } catch (error: any) {
      toast({ title: 'Error', description: formatErrorMessage(error) || 'Failed to remove agent', variant: 'destructive' });
    }
  };

  // ── Derived values ──────────────────────────────────────────────────────────

  const filteredTeams = teams.filter((team) => {
    const matchesSearch =
      team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (team.description?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    const matchesFilter =
      filterAutoAssign === 'all' ||
      (filterAutoAssign === 'true' && team.allow_auto_assign) ||
      (filterAutoAssign === 'false' && !team.allow_auto_assign);
    return matchesSearch && matchesFilter;
  });

  const allMembers = Object.values(teamMembers).flat();
  const availableCount = allMembers.filter((m) => m.user?.availability_status === 'available').length;

  const stats = {
    total: teams.length,
    autoAssign: teams.filter((t) => t.allow_auto_assign).length,
    totalMembers: Object.values(teamMembers).reduce((sum, m) => sum + m.length, 0),
    available: availableCount,
  };

  const selectedTeam = teams.find((t) => t.id === selectedTeamId) ?? null;
  const selectedMembers = selectedTeamId ? (teamMembers[selectedTeamId] ?? []) : [];

  const filteredSelectedMembers = memberSearch.trim()
    ? selectedMembers.filter((m) =>
        m.user &&
        (`${m.user.first_name} ${m.user.last_name}`.toLowerCase().includes(memberSearch.toLowerCase()) ||
          m.user.email.toLowerCase().includes(memberSearch.toLowerCase()))
      )
    : selectedMembers;

  const filteredAgents = agents.filter(
    (a) =>
      `${a.first_name} ${a.last_name}`.toLowerCase().includes(agentSearchTerm.toLowerCase()) ||
      a.email.toLowerCase().includes(agentSearchTerm.toLowerCase()),
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <TooltipProvider>
    <div className="flex flex-1 flex-col gap-4 py-4 px-4 md:gap-6 md:py-6 lg:px-6 text-foreground">

      {/* ── Page header ── */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-foreground">Teams</h1>
          <p className="text-sm text-muted-foreground">
            Organize agents into teams for routing and collaboration
          </p>
        </div>
        <Button size="sm" onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-1.5" />New Team
        </Button>
      </div>

      {/* ── KPI cards ── */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm @container/card">
          <CardHeader>
            <CardDescription>Total Teams</CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums">{stats.total}</CardTitle>
            <CardAction>
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                <Users className="h-4 w-4 text-primary" />
              </div>
            </CardAction>
          </CardHeader>
        </Card>
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm @container/card">
          <CardHeader>
            <CardDescription>Auto-assign</CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums">{stats.autoAssign}</CardTitle>
            <CardAction>
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                <Zap className="h-4 w-4 text-primary" />
              </div>
            </CardAction>
          </CardHeader>
        </Card>
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm @container/card">
          <CardHeader>
            <CardDescription>Total Members</CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums">{stats.totalMembers}</CardTitle>
            <CardAction>
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                <UserPlus className="h-4 w-4 text-primary" />
              </div>
            </CardAction>
          </CardHeader>
        </Card>
        <Card className="bg-gradient-to-t from-emerald-500/5 to-card shadow-sm @container/card">
          <CardHeader>
            <CardDescription>Available Now</CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">{stats.available}</CardTitle>
            <CardAction>
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950">
                <Activity className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              </div>
            </CardAction>
          </CardHeader>
        </Card>
      </div>

      {/* ── Split panel ── */}
      <div className="flex gap-4 min-h-0" style={{ minHeight: 500 }}>

        {/* LEFT: Teams sidebar */}
        <div className="w-[268px] shrink-0 flex flex-col rounded-xl border bg-card shadow-sm overflow-hidden">
          {/* Sidebar header */}
          <div className="px-3 pt-3 pb-2 border-b">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">All Teams</span>
              <Badge variant="outline" className="text-xs px-1.5 py-0 h-5">{teams.length}</Badge>
            </div>
            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search teams…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 h-7 text-xs"
              />
            </div>
            <Select value={filterAutoAssign} onValueChange={setFilterAutoAssign}>
              <SelectTrigger className="h-7 text-xs dark:bg-muted">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teams</SelectItem>
                <SelectItem value="true">Auto-assign only</SelectItem>
                <SelectItem value="false">Manual only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Team list */}
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="p-3 space-y-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-2.5 px-2 py-2">
                    <Skeleton className="h-8 w-8 rounded-lg shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <Skeleton className="h-3 w-24" />
                      <Skeleton className="h-2 w-16" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredTeams.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center gap-2">
                <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                  <Users className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">No teams found</p>
                {!searchTerm && (
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleOpenDialog()}>
                    <Plus className="h-3 w-3 mr-1" />Create team
                  </Button>
                )}
              </div>
            ) : (
              filteredTeams.map((team) => {
                const members = teamMembers[team.id] ?? [];
                const isSelected = selectedTeamId === team.id;
                const colorClass = getTeamColor(team.name);
                return (
                  <button
                    key={team.id}
                    onClick={() => { setSelectedTeamId(team.id); setMemberSearch(''); }}
                    className={cn(
                      'relative w-full text-left border-b border-border/40 border-l-[3px] py-2.5 pl-3 pr-10 transition-colors group focus-visible:outline-none',
                      isSelected ? 'bg-accent border-l-primary' : 'border-l-transparent hover:bg-muted/50',
                    )}
                  >
                    <div className="flex items-start gap-2.5">
                      <div className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold mt-0.5', colorClass)}>
                        {getTeamInitials(team.name)}
                      </div>
                      <div className="min-w-0 flex-1 overflow-visible">
                        <div className="flex items-center gap-1.5 pr-1">
                          <span className="text-sm font-medium truncate text-foreground leading-tight">{team.name}</span>
                          {team.allow_auto_assign && (
                            <Zap className="h-3 w-3 shrink-0 text-amber-500" />
                          )}
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {members.length} member{members.length !== 1 ? 's' : ''}
                        </p>
                        {members.some((m) => m.user) && (
                          <div className="mt-1.5 overflow-visible">
                            <MemberFacepile members={members} max={4} size={15} />
                          </div>
                        )}
                      </div>
                    </div>
                    <ChevronRight
                      className={cn(
                        'pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground transition-opacity',
                        isSelected ? 'opacity-70' : 'opacity-0 group-hover:opacity-40',
                      )}
                    />
                  </button>
                );
              })
            )}
          </ScrollArea>

          {/* Footer */}
          {!loading && filteredTeams.length > 0 && (
            <div className="px-3 py-2 border-t bg-muted/20">
              <p className="text-[11px] text-muted-foreground">
                {filteredTeams.length} team{filteredTeams.length !== 1 ? 's' : ''}
                {searchTerm ? ` for "${searchTerm}"` : ''}
              </p>
            </div>
          )}
        </div>

        {/* RIGHT: Team detail panel */}
        <div className="flex-1 rounded-xl border bg-card shadow-sm overflow-hidden flex flex-col min-w-0">
          {!selectedTeam ? (
            /* Empty state */
            <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center p-10">
              <div className="relative">
                <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center border border-primary/10">
                  <Users className="h-9 w-9 text-primary/40" />
                </div>
                <div className="absolute -bottom-1.5 -right-1.5 h-6 w-6 rounded-full bg-emerald-100 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 flex items-center justify-center">
                  <Zap className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
              <div className="max-w-xs">
                <p className="text-base font-semibold text-foreground">Select a team</p>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                  Pick a team from the list to view members, manage assignments, and configure routing settings.
                </p>
              </div>
              <Button size="sm" onClick={() => handleOpenDialog()} className="mt-1">
                <Plus className="h-4 w-4 mr-1.5" />Create New Team
              </Button>
            </div>
          ) : (
            <div className="flex flex-col h-full overflow-hidden">

              {/* ── Hero Header ── */}
              <div className="relative overflow-hidden shrink-0">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/8 via-primary/3 to-transparent pointer-events-none" />
                <div className="relative px-5 pt-5 pb-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={cn(
                        'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-lg font-bold',
                        getTeamColor(selectedTeam.name)
                      )}>
                        {getTeamInitials(selectedTeam.name)}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h2 className="text-lg font-bold text-foreground leading-tight">{selectedTeam.name}</h2>
                          {selectedTeam.allow_auto_assign ? (
                            <Badge variant="outline" className="text-xs border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-400 shrink-0">
                              <Zap className="h-2.5 w-2.5 mr-1" />Auto-assign
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs text-muted-foreground shrink-0">
                              <ZapOff className="h-2.5 w-2.5 mr-1" />Manual
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">
                          {selectedTeam.description || 'No description provided'}
                        </p>
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => handleOpenMembersDialog(selectedTeam)}>
                        <UserPlus className="h-3.5 w-3.5 mr-1" />Add Members
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => handleOpenDialog(selectedTeam)}>
                        <Pencil className="h-3.5 w-3.5 mr-1" />Edit
                      </Button>
                      <Button
                        size="sm" variant="outline" className="h-8 text-xs text-destructive hover:text-destructive border-destructive/20 hover:bg-destructive/5"
                        onClick={() => { setTeamToDelete(selectedTeam); setDeleteConfirmOpen(true); }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Stats Strip ── */}
              {(() => {
                const members = selectedMembers.filter((m) => m.user);
                const leads = members.filter((m) => m.is_admin);
                const available = members.filter((m) => m.user.availability_status === 'available');
                return (
                  <div className="grid shrink-0 grid-cols-1 gap-3 border-b border-border/60 px-5 py-4 sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
                    <Card className="rounded-xl border-border/60 bg-card shadow-sm">
                      <CardContent className="flex flex-col gap-2 p-4 sm:p-5">
                        <div className="flex items-center gap-1.5">
                          <div className="flex h-5 w-5 items-center justify-center rounded-md bg-primary/15">
                            <Users className="h-3 w-3 text-primary" />
                          </div>
                          <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                            Members
                          </span>
                        </div>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-3xl font-bold tabular-nums leading-none text-primary">
                            {members.length}
                          </span>
                          {leads.length > 0 && (
                            <span className="text-xs text-muted-foreground">
                              {leads.length} lead{leads.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        {members.length > 0 ? (
                          <>
                            <MemberFacepile members={members} max={6} size={20} />
                            <p className="text-xs leading-none text-muted-foreground">
                              {available.length} available now
                            </p>
                          </>
                        ) : (
                          <p className="text-xs leading-none text-muted-foreground">No members yet</p>
                        )}
                      </CardContent>
                    </Card>

                    <Card className="rounded-xl border-border/60 bg-card shadow-sm">
                      <CardContent className="flex flex-col gap-2 p-4 sm:p-5">
                        <div className="flex items-center gap-1.5">
                          <div
                            className={cn(
                              'flex h-5 w-5 items-center justify-center rounded-md',
                              selectedTeam.allow_auto_assign
                                ? 'bg-amber-100 dark:bg-amber-950'
                                : 'bg-muted'
                            )}
                          >
                            {selectedTeam.allow_auto_assign ? (
                              <Zap className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                            ) : (
                              <ZapOff className="h-3 w-3 text-muted-foreground" />
                            )}
                          </div>
                          <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                            Auto-assign
                          </span>
                        </div>
                        <span
                          className={cn(
                            'text-2xl font-bold leading-none',
                            selectedTeam.allow_auto_assign
                              ? 'text-amber-600 dark:text-amber-400'
                              : 'text-muted-foreground'
                          )}
                        >
                          {selectedTeam.allow_auto_assign ? 'Enabled' : 'Disabled'}
                        </span>
                        <p className="text-xs leading-relaxed text-muted-foreground">
                          {selectedTeam.allow_auto_assign
                            ? 'Conversations routed automatically'
                            : 'Manual assignment only'}
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="rounded-xl border-border/60 bg-card shadow-sm">
                      <CardContent className="flex flex-col gap-2 p-4 sm:p-5">
                        <div className="flex items-center gap-1.5">
                          <div className="flex h-5 w-5 items-center justify-center rounded-md bg-muted">
                            <CalendarDays className="h-3 w-3 text-muted-foreground" />
                          </div>
                          <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                            Created
                          </span>
                        </div>
                        <span className="text-xl font-bold leading-none text-foreground">
                          {getRelativeTime(selectedTeam.created_at)}
                        </span>
                        <p className="text-xs leading-none text-muted-foreground">
                          {formatDate(selectedTeam.created_at)}
                        </p>
                        <p className="flex items-center gap-1 text-xs leading-none text-muted-foreground">
                          <Activity className="h-2.5 w-2.5" />
                          Updated {getRelativeTime(selectedTeam.updated_at)}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                );
              })()}

              {/* ── Members section ── */}
              <div className="flex items-center justify-between px-5 pt-4 pb-2 shrink-0">
                <h3 className="text-sm font-semibold text-foreground">
                  Members
                  {selectedMembers.length > 0 && (
                    <span className="ml-2 text-xs font-normal text-muted-foreground">{selectedMembers.length}</span>
                  )}
                </h3>
                {selectedMembers.length > 4 && (
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Filter members…"
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      className="pl-8 h-7 text-xs w-44"
                    />
                  </div>
                )}
              </div>

              {/* Members table */}
              <ScrollArea className="flex-1">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-[38%] text-xs">Member</TableHead>
                      <TableHead className="text-xs">Role</TableHead>
                      <TableHead className="text-xs">Status</TableHead>
                      <TableHead className="text-xs">Team Lead</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSelectedMembers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-14">
                          <div className="flex flex-col items-center gap-3 text-muted-foreground">
                            {memberSearch ? (
                              <>
                                <Search className="h-7 w-7 opacity-30" />
                                <p className="text-sm">No members match "{memberSearch}"</p>
                              </>
                            ) : (
                              <>
                                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                                  <UserPlus className="h-6 w-6 opacity-50" />
                                </div>
                                <div className="space-y-1">
                                  <p className="text-sm font-medium text-foreground">No members yet</p>
                                  <p className="text-xs">Add agents to get started</p>
                                </div>
                                <Button size="sm" variant="outline" onClick={() => handleOpenMembersDialog(selectedTeam)}>
                                  <UserPlus className="h-3.5 w-3.5 mr-1.5" />Add Members
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredSelectedMembers.map((member) =>
                        member.user ? (
                          <TableRow key={member.id} className="group">
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="relative shrink-0">
                                  <Avatar className="h-8 w-8">
                                    <AvatarImage src={member.user.avatar_url} />
                                    <AvatarFallback className="text-xs bg-primary/10 text-primary">
                                      {member.user.first_name?.[0]}{member.user.last_name?.[0]}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className={cn(
                                    'absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-background',
                                    AVAIL_DOT[member.user.availability_status] ?? AVAIL_DOT.offline,
                                  )} />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium truncate text-foreground">
                                    {member.user.first_name} {member.user.last_name}
                                  </p>
                                  <p className="text-xs text-muted-foreground truncate">{member.user.email}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs capitalize">
                                {member.user.role.replace(/_/g, ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={cn('text-xs capitalize', AVAIL_BADGE[member.user.availability_status] ?? AVAIL_BADGE.offline)}
                              >
                                <span className={cn('mr-1.5 h-1.5 w-1.5 rounded-full inline-block shrink-0',
                                  AVAIL_DOT[member.user.availability_status] ?? AVAIL_DOT.offline
                                )} />
                                {member.user.availability_status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {member.is_admin && (
                                <Badge variant="secondary" className="text-xs gap-1 bg-primary/10 text-primary border-primary/20">
                                  <ShieldCheck className="h-3 w-3" />Lead
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={() => handleRemoveAgentFromTeam(selectedTeam.id, member.user.id)}
                                  >
                                    <UserMinus className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Remove from team</TooltipContent>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ) : null,
                      )
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          )}
        </div>
      </div>

      {/* ── Team Form Dialog ── */}
      <Dialog open={openDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className={cn('flex h-9 w-9 items-center justify-center rounded-lg text-sm font-bold',
                formData.name ? getTeamColor(formData.name) : 'bg-primary/10 text-primary'
              )}>
                {formData.name ? getTeamInitials(formData.name) : <Users className="h-4 w-4" />}
              </div>
              <DialogTitle>{editingTeam ? 'Edit Team' : 'New Team'}</DialogTitle>
            </div>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="team-name" className="text-sm font-medium">
                Team Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="team-name"
                autoFocus
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. Customer Success"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="team-description" className="text-sm font-medium">Description</Label>
              <textarea
                id="team-description"
                className="flex min-h-[72px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional team description"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/20 px-4 py-3">
              <div>
                <Label htmlFor="auto-assign" className="cursor-pointer text-sm font-medium flex items-center gap-1.5">
                  <Zap className="h-3.5 w-3.5 text-amber-500" />
                  Auto-assign conversations
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Automatically route incoming conversations to this team
                </p>
              </div>
              <Switch
                id="auto-assign"
                checked={formData.allow_auto_assign}
                onCheckedChange={(checked) => setFormData({ ...formData, allow_auto_assign: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={!formData.name.trim()}>
              {editingTeam ? 'Save Changes' : 'Create Team'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add / Manage Members Dialog ── */}
      <Dialog open={openMembersDialog} onOpenChange={() => setOpenMembersDialog(false)}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-center gap-3">
              {membersDialogTeam && (
                <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg text-xs font-bold shrink-0',
                  getTeamColor(membersDialogTeam.name)
                )}>
                  {getTeamInitials(membersDialogTeam.name)}
                </div>
              )}
              <div>
                <DialogTitle>Manage Members</DialogTitle>
                {membersDialogTeam && (
                  <p className="text-xs text-muted-foreground mt-0.5">{membersDialogTeam.name}</p>
                )}
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-3 flex-1 overflow-hidden flex flex-col min-h-0 py-2">
            <div className="relative shrink-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search agents…"
                value={agentSearchTerm}
                onChange={(e) => setAgentSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center justify-between shrink-0">
              <p className="text-xs text-muted-foreground">{selectedAgentIds.length} selected</p>
              <button
                className="text-xs text-primary hover:underline"
                onClick={() => setSelectedAgentIds(agents.map((a) => a.id))}
              >
                Select all
              </button>
            </div>
            <ScrollArea className="h-[min(50vh,24rem)] border rounded-lg">
              <div className="p-1">
                {filteredAgents.map((agent) => {
                  const isSelected = selectedAgentIds.includes(agent.id);
                  return (
                    <div
                      key={agent.id}
                      className={cn(
                        'flex items-center gap-3 rounded-md px-3 py-2.5 cursor-pointer transition-colors',
                        isSelected ? 'bg-accent/60' : 'hover:bg-muted/50',
                      )}
                      onClick={() =>
                        setSelectedAgentIds((prev) =>
                          prev.includes(agent.id) ? prev.filter((id) => id !== agent.id) : [...prev, agent.id],
                        )
                      }
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) =>
                          setSelectedAgentIds((prev) =>
                            checked ? [...prev, agent.id] : prev.filter((id) => id !== agent.id),
                          )
                        }
                      />
                      <div className="relative shrink-0">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={agent.avatar_url} />
                          <AvatarFallback className="text-xs bg-primary/10 text-primary">{agent.first_name[0]}</AvatarFallback>
                        </Avatar>
                        <span className={cn('absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-background',
                          AVAIL_DOT[agent.availability_status] ?? AVAIL_DOT.offline
                        )} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate text-foreground">{agent.first_name} {agent.last_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{agent.email}</p>
                      </div>
                      <Badge
                        variant="outline"
                        className={cn('text-xs capitalize shrink-0', AVAIL_BADGE[agent.availability_status] ?? AVAIL_BADGE.offline)}
                      >
                        {agent.availability_status}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => setOpenMembersDialog(false)}>Cancel</Button>
            <Button onClick={handleAddAgentsToTeam}>
              Update Members
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirmation ── */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Team</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>"{teamToDelete?.name}"</strong>? This action cannot be undone.
              {teamToDelete && (teamMembers[teamToDelete.id]?.length ?? 0) > 0 && (
                <span className="flex items-start gap-2 mt-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-950/30 px-3 py-2 text-amber-700 dark:text-amber-400">
                  <Info className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{teamMembers[teamToDelete.id]?.length} member(s) will be removed from this team.</span>
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setDeleteConfirmOpen(false); setTeamToDelete(null); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => teamToDelete && handleDelete(teamToDelete)}
            >
              Delete Team
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
    </TooltipProvider>
  );
};

export default TeamsPage;
